import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API, getAuthHeaders } from '../shared';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SearchableSelect } from './ui/searchable-select';
import { Textarea } from './ui/textarea';
import { Users, Phone, BellRing, TrendingUp, Target, DollarSign, CheckCircle2, Plus, Trash2, Calendar } from 'lucide-react';
import WhatsAppButton from './WhatsAppButton';

const PIPELINE_STAGES = [
  { id: 'lead', label: 'عميل محتمل', color: 'bg-gray-100 text-gray-700' },
  { id: 'interested', label: 'مهتم', color: 'bg-brand-100 text-brand-700' },
  { id: 'negotiation', label: 'تفاوض', color: 'bg-amber-100 text-amber-700' },
  { id: 'booked', label: 'محجوز', color: 'bg-purple-100 text-purple-700' },
  { id: 'paid', label: 'مدفوع', color: 'bg-green-100 text-green-700' },
  { id: 'traveling', label: 'مسافر', color: 'bg-cyan-100 text-cyan-700' },
  { id: 'completed', label: 'منتهٍ', color: 'bg-emerald-100 text-emerald-700' },
  { id: 'lost', label: 'مفقود', color: 'bg-red-100 text-red-700' }
];

const CRMDashboard = () => {
  useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followupDialog, setFollowupDialog] = useState(false);
  const [fuForm, setFuForm] = useState({ client_id: '', title: '', description: '', due_date: new Date().toISOString().split('T')[0] });

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [statsR, fupsR, clR] = await Promise.all([
        axios.get(`${API}/crm/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/crm/followups?only_due=true`, { headers: getAuthHeaders() }),
        axios.get(`${API}/clients`, { headers: getAuthHeaders() })
      ]);
      setStats(statsR.data);
      setFollowups(fupsR.data || []);
      setClients(clR.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const markDone = async (id) => {
    try {
      await axios.put(`${API}/crm/followups/${id}`, { is_done: true }, { headers: getAuthHeaders() });
      fetchAll();
    } catch (e) { alert('خطأ'); }
  };

  const deleteFu = async (id) => {
    if (!window.confirm('حذف هذا التذكير؟')) return;
    await axios.delete(`${API}/crm/followups/${id}`, { headers: getAuthHeaders() });
    fetchAll();
  };

  const saveFollowup = async () => {
    if (!fuForm.client_id || !fuForm.title.trim()) { alert('اختر العميل وأدخل العنوان'); return; }
    try {
      await axios.post(`${API}/crm/followups`, fuForm, { headers: getAuthHeaders() });
      setFollowupDialog(false);
      setFuForm({ client_id: '', title: '', description: '', due_date: new Date().toISOString().split('T')[0] });
      fetchAll();
    } catch (e) { alert(e.response?.data?.detail || 'خطأ'); }
  };

  if (loading) return <div className="p-10 text-center text-gray-500">جاري التحميل...</div>;
  if (!stats) return <div className="p-10 text-center text-gray-500">لا توجد بيانات</div>;

  const maxFunnel = Math.max(...Object.values(stats.funnel || {}), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📊 لوحة CRM — العملاء والمتابعات</h1>
          <p className="text-sm text-gray-600 mt-1">نظرة شاملة على المبيعات، التحويلات، والمتابعات</p>
        </div>
        <Button onClick={() => setFollowupDialog(true)} className="bg-brand-600 hover:bg-brand-700" data-testid="add-followup-btn">
          <Plus className="h-4 w-4 ml-2" /> تذكير جديد
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-r-4 border-r-brand-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">إجمالي العملاء</p>
                <p className="text-3xl font-bold text-brand-700 font-mono">{stats.total_clients}</p>
              </div>
              <Users className="h-10 w-10 text-brand-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-r-4 border-r-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">معدل التحويل</p>
                <p className="text-3xl font-bold text-green-700 font-mono">{stats.conversion_rate}%</p>
                <p className="text-xs text-gray-400">من Lead إلى محجوز</p>
              </div>
              <TrendingUp className="h-10 w-10 text-green-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-r-4 border-r-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">قيمة Pipeline</p>
                <p className="text-2xl font-bold text-amber-700 font-mono">{(stats.pipeline_value || 0).toLocaleString()}</p>
                <p className="text-xs text-gray-400">دج (مهتم + تفاوض + محجوز)</p>
              </div>
              <DollarSign className="h-10 w-10 text-amber-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-r-4 border-r-red-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">متابعات معلقة</p>
                <p className="text-3xl font-bold text-red-700 font-mono">{stats.pending_followups}</p>
                <p className="text-xs text-gray-400">تذكيرات لم تُنفذ</p>
              </div>
              <BellRing className="h-10 w-10 text-red-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Funnel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Target className="h-5 w-5 text-purple-600" /> Sales Funnel — مراحل المبيعات
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {PIPELINE_STAGES.map(s => {
              const count = stats.funnel[s.id] || 0;
              const width = (count / maxFunnel) * 100;
              return (
                <div key={s.id} className="flex items-center gap-2">
                  <div className="w-24 text-xs text-gray-600">{s.label}</div>
                  <div className="flex-1 bg-gray-100 rounded-full h-7 relative overflow-hidden">
                    <div className={`h-full rounded-full ${s.color} transition-all`} style={{ width: `${width}%` }} />
                    <span className="absolute inset-0 flex items-center justify-end pr-3 text-xs font-bold text-gray-700">{count}</span>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Lead Sources */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">📡 مصادر العملاء</CardTitle>
          </CardHeader>
          <CardContent>
            {(stats.sources || []).length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">لا توجد بيانات مصادر بعد. أضف <code>lead_source</code> للعملاء.</p>
            ) : (
              <div className="space-y-2">
                {stats.sources.map(s => (
                  <div key={s.source} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                    <span className="text-sm">{s.source}</span>
                    <Badge variant="secondary">{s.count}</Badge>
                  </div>
                ))}
              </div>
            )}
            {(stats.types || []).length > 0 && (
              <>
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs text-gray-500 mb-2">حسب النوع:</p>
                  <div className="flex gap-2 flex-wrap">
                    {stats.types.map(t => (
                      <Badge key={t.type} variant="outline">{t.type}: {t.count}</Badge>
                    ))}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pending Follow-ups */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BellRing className="h-5 w-5 text-red-600" /> المتابعات المعلقة ({followups.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {followups.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">🎉 لا توجد متابعات معلقة. كل التذكيرات منفذة!</p>
          ) : (
            <div className="space-y-2">
              {followups.map(fu => (
                <div key={fu.id} className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded">
                  <Calendar className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{fu.title}</span>
                      <Badge variant="outline" className="text-xs">{fu.due_date}</Badge>
                    </div>
                    <p className="text-xs text-gray-600">العميل: {fu.client_name}</p>
                    {fu.description && <p className="text-xs text-gray-500 mt-1">{fu.description}</p>}
                  </div>
                  <div className="flex gap-1">
                    {fu.client_phone && (
                      <WhatsAppButton
                        phone={fu.client_phone}
                        clientName={fu.client_name}
                        packageName=""
                        defaultTemplate="reminder"
                        variant="icon-only"
                      />
                    )}
                    {fu.client_phone && (
                      <Button size="sm" variant="outline" onClick={() => window.open(`tel:${fu.client_phone}`)}>
                        <Phone className="h-4 w-4 text-brand-600" />
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => markDone(fu.id)} className="bg-green-50 hover:bg-green-100 border-green-300 text-green-700">
                      <CheckCircle2 className="h-4 w-4" /> تم
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteFu(fu.id)}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Follow-up Dialog */}
      <Dialog open={followupDialog} onOpenChange={setFollowupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>تذكير متابعة جديد</DialogTitle>
            <DialogDescription>أنشئ تذكيراً للاتصال بالعميل في تاريخ محدد</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>العميل *</Label>
              <SearchableSelect
                value={fuForm.client_id}
                onChange={(v) => setFuForm({ ...fuForm, client_id: v })}
                options={clients.map(c => ({
                  value: c.id,
                  label: c.name,
                  hint: c.phone || c.email || '',
                  search: `${c.name} ${c.phone || ''} ${c.email || ''}`,
                }))}
                placeholder="اختر عميل أو ابحث عنه..."
                searchPlaceholder="🔎 ابحث بالاسم أو الهاتف..."
                emptyText="لا يوجد عميل مطابق"
                testId="crm-followup-client-select"
              />
            </div>
            <div>
              <Label>عنوان التذكير *</Label>
              <Input value={fuForm.title} onChange={e => setFuForm({ ...fuForm, title: e.target.value })} placeholder="مثال: متابعة عرض عمرة رجب" />
            </div>
            <div>
              <Label>الوصف</Label>
              <Textarea value={fuForm.description} onChange={e => setFuForm({ ...fuForm, description: e.target.value })} rows={2} />
            </div>
            <div>
              <Label>تاريخ التذكير *</Label>
              <Input type="date" value={fuForm.due_date} onChange={e => setFuForm({ ...fuForm, due_date: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setFollowupDialog(false)}>إلغاء</Button>
              <Button onClick={saveFollowup} className="bg-brand-600 hover:bg-brand-700">حفظ</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRMDashboard;
